/**
 * CustomerERP — Faz 0 Kontrol Paneli.
 * Yalnız teknik/yönetim durumu: firma bilgisi, depolama durumu, hızlı geçişler
 * ve son audit kayıtları. MetaERP business widget'ları (eski Dashboard.jsx)
 * kodda korunur ancak gösterilmez.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { canAccess } from "@/components/Layout";
import { PageHeader, Card } from "@/components/UI";
import StorageHealthPills from "@/components/StorageHealthPills";
import { CustomerErpWidgets } from "@/components/dashboard/CustomerErpWidgets";
import { Users, Users2, ShoppingCart, Package, Boxes, Settings, ScrollText, Archive, Building2, HandCoins, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

const LINKS = [
  { to: "/sales-orders", label: "Satış Siparişleri", desc: "Sipariş, proforma ve stok rezervasyonu", icon: ShoppingCart, tone: "emerald", perm: "sales_orders" },
  { to: "/products", label: "Ürünler", desc: "Ürün kartları, fiyatlar, görseller", icon: Package, tone: "amber", perm: "products" },
  { to: "/stock", label: "Stok", desc: "Stok özeti, düzeltme ve lotlar", icon: Boxes, tone: "teal", perm: "inventory" },
  { to: "/finance", label: "Muhasebe", desc: "Müşteri / tedarikçi cari, tahsilat, ödeme", icon: HandCoins, tone: "emerald", perm: "finance" },
  { to: "/customers", label: "Müşteriler", desc: "Müşteri kartları, tip ve vade bilgileri", icon: Users2, tone: "sky", perm: "customers" },
  { to: "/users", label: "Kullanıcılar & Yetkiler", desc: "Hesaplar, roller ve sayfa yetkileri", icon: Users, tone: "sky", perm: "users" },
  { to: "/settings", label: "Sistem Ayarları", desc: "Firma bilgileri, e-posta, Telegram, güvenlik", icon: Settings, tone: "slate", perm: "settings" },
  { to: "/audit-log", label: "Audit Log", desc: "Kim, ne zaman, neyi değiştirdi", icon: ScrollText, tone: "indigo", perm: "admin_only" },
  { to: "/backup", label: "Yedekleme", desc: "Şifreli veritabanı yedekleri", icon: Archive, tone: "teal", perm: "admin_only" },
];
const TONE = {
  sky: "bg-sky-50 text-sky-600", slate: "bg-slate-100 text-slate-600",
  indigo: "bg-indigo-50 text-indigo-600", teal: "bg-teal-50 text-teal-600", amber: "bg-amber-50 text-amber-600", emerald: "bg-emerald-50 text-emerald-600",
};
const REQUIRED = ["company_name", "trade_name", "address", "phone", "email", "tax_office", "tax_no"];

export default function HomeDashboard() {
  const { user } = useAuth();
  const { brandName } = useBranding();
  const isAdmin = user?.role === "admin";
  const [profile, setProfile] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/company-profile").then(setProfile).catch(() => setProfile({}));
    if (isAdmin) api.get("/audit", { limit: 5 }).then((r) => setAudit(r || [])).catch(() => setAudit([]));
  }, [isAdmin]);

  const filled = profile ? REQUIRED.filter((k) => (profile[k] || "").toString().trim()).length : 0;
  const complete = profile && filled === REQUIRED.length;

  return (
    <div data-testid="faz0-dashboard">
      <PageHeader title="Kontrol Paneli" description={`${brandName} — sistem durumu`}
        actions={isAdmin ? <StorageHealthPills /> : null} />

      <Card className="p-5 mb-5 flex items-center gap-4" data-testid="dash-company-card">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${complete ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
          <Building2 size={20} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Firma Bilgileri</div>
          <div className="text-xs text-slate-500 flex items-center gap-1" data-testid="dash-company-status">
            {complete ? <><CheckCircle2 size={12} className="text-emerald-600" /> Tamamlandı</>
              : <><AlertCircle size={12} className="text-amber-600" /> {filled}/{REQUIRED.length} alan dolu — belgelerde ve e-postalarda kullanılacak bilgileri tamamlayın</>}
          </div>
        </div>
        <Link to="/settings" className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1" data-testid="dash-company-link">
          Düzenle <ArrowRight size={14} />
        </Link>
      </Card>

      <CustomerErpWidgets />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        {LINKS.filter((l) => canAccess(user, l.perm)).map(({ to, label, desc, icon: Icon, tone }) => (
          <Link key={to} to={to} data-testid={`dash-link-${to.slice(1)}`}
            className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${TONE[tone]}`}><Icon size={18} /></div>
            <div className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700">{label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
          </Link>
        ))}
      </div>

      {isAdmin && (
        <Card className="p-5" data-testid="dash-recent-audit">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-800">Son İşlemler</div>
            <Link to="/audit-log" className="text-xs text-indigo-600 hover:text-indigo-800">Tümü</Link>
          </div>
          {audit.length === 0 ? (
            <div className="text-xs text-slate-500">Henüz kayıt yok.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {audit.map((a, i) => (
                <li key={i} className="py-2 text-xs flex gap-3">
                  <span className="font-mono text-slate-400 w-36 shrink-0">{(a.timestamp || "").replace("T", " ").slice(0, 16)}</span>
                  <span className="text-slate-700">{a.user_email || a.user_id || "—"}</span>
                  <span className="text-slate-500">{a.action} · {a.entity}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
